<template>
  <header
    class="header fixed left-0 right-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-(--duration-gob) ease-gob-out"
    :class="isScrolled
      ? 'is-compacto bg-surface-strong/85 shadow-[0_2px_20px_rgba(33,37,41,0.12)] backdrop-blur-md'
      : 'bg-surface'"
  >
    <div class="gob-container">
      <div
        class="flex items-center justify-between transition-[padding] duration-(--duration-gob) ease-gob-out"
        :class="isScrolled ? 'py-2 lg:py-3' : 'py-4 lg:py-5'"
      >
        <!-- Logo -->
        <div class="logo">
          <router-link to="/" class="logo-link flex items-center gap-2 no-underline">
            <img
              src="@/assets/dubs-logo-colors.svg"
              alt="Gob 24/7"
              class="logo-imagen"
            />
          </router-link>
        </div>

        <!-- Fondo del menú móvil: cierra el panel al tocar fuera. -->
        <div v-if="isMenuOpen" class="fixed inset-0 bg-main/40 lg:hidden" aria-hidden="true" @click="closeMenu"></div>

        <!-- ===== NAVEGACIÓN CON SUBMENÚ ===== -->
        <nav
          id="menu-principal"
          ref="menu"
          class="nav fixed -right-full top-0 flex h-screen w-75 flex-col items-start gap-6 bg-surface px-8 py-20 text-main transition-all duration-300 lg:static lg:flex lg:h-auto lg:w-auto lg:flex-row lg:items-center lg:gap-8 lg:bg-transparent lg:p-0"
          :class="{ 'right-0 shadow-strong': isMenuOpen }"
        >
          <router-link class="gob-nav-link" to="/" @click="closeMenu">Inicio</router-link>

          <router-link class="gob-nav-link" to="/problema" @click="closeMenu">El Reto Territorial</router-link>

          <!-- ===== SUBMENÚ: ECOSISTEMA GOB ===== -->
          <div class="submenu-wrapper relative w-full lg:w-auto lg:mx-1" @mouseenter="openSubmenu" @mouseleave="handleSubmenuLeave">
            <button
              ref="submenuToggle"
              type="button"
              aria-controls="submenu-ecosistema"
              :aria-expanded="String(isSubmenuOpen)"
              @click="toggleSubmenu"
              class="gob-nav-link submenu-toggle flex items-center gap-1"
              :class="{ 'text-primary': isSubmenuOpen, 'es-activo': enSolucion }"
            >
              Ecosistema Gob
              <svg class="w-4 h-4 transition-transform duration-(--duration-gob) ease-gob-out" :class="{ 'rotate-180': isSubmenuOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>

            <!-- Submenú desplegable: abre y cierra con fundido y un descenso corto -->
            <Transition name="submenu">
              <div
                v-show="isSubmenuOpen"
                id="submenu-ecosistema"
                class="submenu mt-2 w-full rounded-xl bg-white p-2 shadow-xl lg:absolute lg:left-0 lg:w-72"
                @mouseenter="openSubmenu"
                @mouseleave="handleSubmenuLeave"
              >
                <!-- Nombres y rutas desde src/data/soluciones.js: el mismo registro de las subpáginas. -->
                <router-link
                  v-for="solucion in soluciones"
                  :key="solucion.id"
                  class="submenu-item block rounded-lg px-4 py-3 text-sm font-medium text-main transition-colors duration-(--duration-gob) ease-gob-out hover:bg-surface-strong hover:text-primary"
                  :to="solucion.ruta"
                  @click="closeMenu"
                >
                  {{ solucion.nombre }}
                </router-link>
              </div>
            </Transition>
          </div>

          <router-link class="gob-nav-link" to="/trayectoria" @click="closeMenu">Trayectoria</router-link>

          <router-link class="gob-nav-link" to="/como-empezar" @click="closeMenu">¿Cómo Empezar?</router-link>

          <GobButton
            class="nav-cta"
            to="/contacto"
            variante="primario"
            tamano="sm"
            bloque="nunca"
            @click="closeMenu"
          >
            Solicitar demo
          </GobButton>
        </nav>

        <button class="menu-toggle relative z-10 flex flex-col gap-1.5 rounded-md border border-[#DEE2E6] bg-white/90 p-2 shadow-sm lg:hidden" ref="menuToggle" type="button" aria-controls="menu-principal" :aria-expanded="String(isMenuOpen)" @click="toggleMenu" aria-label="Menú">
          <span class="block h-0.5 w-6 rounded bg-main transition-transform duration-(--duration-gob) ease-gob-out" :class="{ 'translate-y-2 rotate-45': isMenuOpen }"></span>
          <span class="block h-0.5 w-6 rounded bg-main transition-opacity duration-(--duration-gob) ease-gob-out" :class="{ 'opacity-0': isMenuOpen }"></span>
          <span class="block h-0.5 w-6 rounded bg-main transition-transform duration-(--duration-gob) ease-gob-out" :class="{ '-translate-y-2 -rotate-45': isMenuOpen }"></span>
        </button>
      </div>
    </div>
  </header>
</template>

<script>
import GobButton from '@/components/GobButton.vue'
import { SOLUCIONES } from '@/data/soluciones'

/**
 * SiteHeader
 * Encapsula la navegación principal de la aplicación.
 * Controla el comportamiento del menú móvil, el estado sticky al hacer scroll
 * y el desplegable del grupo "La Solución".
 */
export default {
  name: 'SiteHeader',
  components: {
    GobButton
  },
  data() {
    return {
      /** Soluciones del submenú «Ecosistema Gob», desde el registro único. */
      soluciones: SOLUCIONES,
      /**
       * isScrolled
       * Indica si la página ya superó cierto desplazamiento. Activa el fondo y la sombra
       * del header y lo compacta: menos padding vertical y logo escalado al 80 %.
       */
      isScrolled: false,
      /**
       * isMenuOpen
       * Controla la apertura del menú en dispositivos móviles.
       */
      isMenuOpen: false,
      /**
       * isSubmenuOpen
       * Controla la visibilidad del submenú de soluciones.
       */
      isSubmenuOpen: false,
      /**
       * submenuTimer
       * Temporizador para cerrar el submenú con una pequeña demora.
       */
      submenuTimer: null
    }
  },
  computed: {
    /* Mantiene «Ecosistema Gob» marcado mientras se navega cualquiera de sus cuatro soluciones,
       para que la persona sepa siempre en qué sección está. */
    enSolucion() {
      return this.soluciones.some((solucion) => solucion.ruta === this.$route.path)
    }
  },
  watch: {
    /* Bloquea el scroll del documento mientras el panel móvil está abierto. */
    isMenuOpen(abierto) {
      document.documentElement.style.overflow = abierto ? 'hidden' : ''
    }
  },
  mounted() {
    // Escucha el scroll para el estado sticky; passive porque nunca cancela el desplazamiento.
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    // Si la página se recarga ya desplazada, el header arranca compacto.
    this.handleScroll()
    // Permite cerrar el menú móvil y el submenú con la tecla Escape.
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    // Limpia los listeners para evitar fugas de memoria.
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('keydown', this.handleKeydown)
    document.documentElement.style.overflow = ''
  },
  methods: {
    /**
     * handleScroll
     * Cambia el estado visual del header cuando el usuario se desplaza.
     * Como el header es fijo, compactarlo no desplaza el contenido de la página.
     */
    handleScroll() {
      this.isScrolled = window.scrollY > 50
    },
    /**
     * handleKeydown
     * Cierra con Escape el submenú o el menú móvil abierto y devuelve el foco
     * al botón que lo controla, siguiendo el patrón disclosure de WAI-ARIA.
     */
    handleKeydown(event) {
      if (event.key === 'Tab' && this.isMenuOpen) {
        this.atraparFoco(event)
        return
      }
      if (event.key !== 'Escape') return

      if (this.isSubmenuOpen) {
        clearTimeout(this.submenuTimer)
        this.submenuTimer = null
        this.isSubmenuOpen = false
        this.$refs.submenuToggle.focus()
        return
      }

      if (this.isMenuOpen) {
        this.isMenuOpen = false
        this.$refs.menuToggle.focus()
      }
    },
    /**
     * toggleMenu
     * Abre o cierra el menú lateral en vistas pequeñas.
     */
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    /**
     * atraparFoco
     * Con el panel móvil abierto, Tab y Shift+Tab circulan solo entre el botón y los enlaces visibles.
     */
    atraparFoco(event) {
      const enfocables = [this.$refs.menuToggle, ...this.$refs.menu.querySelectorAll('a[href], button')]
        .filter((elemento) => elemento.offsetParent !== null)
      const actual = enfocables.indexOf(document.activeElement)
      const paso = event.shiftKey ? -1 : 1
      event.preventDefault()
      enfocables[(actual + paso + enfocables.length) % enfocables.length].focus()
    },
    /**
     * closeMenu
     * Cierra el menú y submenú al navegar o hacer clic en un enlace.
     */
    closeMenu() {
      this.isMenuOpen = false
      this.isSubmenuOpen = false
      if (this.submenuTimer) {
        clearTimeout(this.submenuTimer)
        this.submenuTimer = null
      }
    },
    /**
     * handleSubmenuLeave
     * Detecta cuando el cursor sale del contenedor del submenú y lo cierra.
     */
    handleSubmenuLeave(event) {
      const wrapper = this.$el.querySelector('.submenu-wrapper')
      const nextTarget = event.relatedTarget

      if (!wrapper || !nextTarget || !wrapper.contains(nextTarget)) {
        this.closeSubmenu()
      }
    },
    /**
     * toggleSubmenu
     * Alterna la apertura del submenú de navegación.
     */
    toggleSubmenu() {
      this.isSubmenuOpen = !this.isSubmenuOpen
    },
    /**
     * openSubmenu
     * Abre el submenú con un pequeño retraso para un comportamiento más suave.
     */
    openSubmenu() {
      if (this.submenuTimer) {
        clearTimeout(this.submenuTimer)
      }

      this.submenuTimer = setTimeout(() => {
        this.isSubmenuOpen = true
      }, 80)
    },
    /**
     * closeSubmenu
     * Cierra el submenú con retraso para evitar cambios bruscos al mover el mouse.
     */
    closeSubmenu() {
      if (this.submenuTimer) {
        clearTimeout(this.submenuTimer)
      }

      this.submenuTimer = setTimeout(() => {
        this.isSubmenuOpen = false
      }, 120)
    }
  }
}
</script>

<style scoped>
/**
 * .logo-imagen
 * Ajusta el tamaño del logo institucional dentro del header.
 */
.logo {
  display: flex;
  align-items: center;
}

.logo-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform-origin: left center;
  transition: transform var(--duration-gob) var(--ease-gob-out);
}

.header.is-compacto .logo-link {
  transform: scale(0.8);
}

.logo-imagen {
  height: 54px;
  width: auto;
  display: block;
  object-fit: contain;
  filter: drop-shadow(0 2px 6px rgba(41, 46, 255, 0.12));
}

/**
 * .text-mint
 * Clase auxiliar heredada que se usa como acento de estado o confirmación.
 */
.text-mint {
  color: #7C3AED;
}

.nav a {
  position: relative;
  text-decoration: none;
  letter-spacing: 0.01em;
}

.nav a.router-link-active:not(.nav-cta),
.nav .submenu-toggle.es-activo {
  color: #292EFF;
}

/**
 * Subrayado de los enlaces de navegación.
 * Crece desde el centro con transform: scaleX(), sin animar el ancho. Se muestra en el
 * enlace de la página activa (y se anima al cambiar de ruta) y, en escritorio, al hover.
 */
.nav a:not(.nav-cta)::after,
.nav .submenu-toggle::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -6px;
  height: 2px;
  background: #292EFF;
  border-radius: 999px;
  transform: scaleX(0);
  transform-origin: center;
  transition: transform var(--duration-gob) var(--ease-gob-out);
}

.nav a.router-link-active:not(.nav-cta)::after,
.nav .submenu-toggle.es-activo::after {
  transform: scaleX(1);
}

.submenu-wrapper {
  margin-top: 0.15rem;
}

.submenu {
  position: relative;
  z-index: 50;
  margin-top: 0.8rem;
  padding: 0.7rem;
  gap: 0.5rem;
  border: 1px solid rgba(222, 226, 230, 0.8);
  background: #FFFFFF;
  box-shadow: 0 18px 36px rgba(33, 37, 41, 0.12);
}

/* Estados de <Transition name="submenu">: fundido y descenso de 8 px al abrir, inverso al cerrar. */
.submenu-enter-active,
.submenu-leave-active {
  transition:
    opacity var(--duration-gob) var(--ease-gob-out),
    transform var(--duration-gob) var(--ease-gob-out);
}

.submenu-enter-from,
.submenu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.submenu-item {
  line-height: 1.35;
  letter-spacing: 0.01em;
  color: #212529;
  padding: 0.7rem 0.85rem;
}

.submenu a + a {
  margin-top: 0.25rem;
}

@media (min-width: 1024px) {
  .nav a:not(.nav-cta):hover::after,
  .nav .submenu-toggle:hover::after {
    transform: scaleX(1);
  }

  .submenu {
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 220px;
    margin-top: 0.9rem;
  }
}

@media (max-width: 1023px) {
  .nav a:not(.nav-cta)::after,
  .nav .submenu-toggle::after {
    bottom: 6px;
  }

  .submenu {
    position: static;
    width: 100%;
    margin-top: 8px;
    padding-left: 16px;
    border-left: 2px solid rgba(41, 46, 255, 0.25);
  }

  .submenu .router-link {
    padding: 10px 16px;
  }
}

@media (max-width: 768px) {
  .logo-imagen {
    height: 42px;
  }
}

@media (max-width: 480px) {
  .logo-imagen {
    height: 34px;
  }
}
</style>
